// Dados da tela "Análise das conversas" (#6313).
//
// A leitura é direta nas tabelas: a RLS já entrega a cada um o que pode ver
// (adm e gerente tudo; a atendente, só o dela). Gerar ou refazer um dia passa
// pela edge function `inbox-analise-diaria`, que só aceita adm e gerente.

import { supabase } from '../../../src/lib/supabase';
import type { LinhaAnalise, MetricasDia } from '../../../supabase/functions/_shared/analiseConversas.ts';

export interface RelatorioDia {
  id: string;
  connection_id: string;
  atendente_id: string | null;
  conexao_nome: string;
  atendente_nome: string | null;
  dia: string;
  conversas_analisadas: number;
  metricas: MetricasDia;
  nota: number | null;
  resumo: string | null;
  pontos_fortes: string[];
  melhorias: Array<{ titulo: string; como_fazer: string; exemplo: string }>;
  insights: string[];
  padroes_objecao: string[];
  recomendacoes_gestao: string[];
  modelo: string | null;
  tokens_entrada: number | null;
  tokens_saida: number | null;
  updated_at: string;
}

export interface AnaliseConversaLinha extends LinhaAnalise {
  id: string;
  connection_id: string;
  dia: string;
  justificativa: string | null;
  mensagens_do_dia: number;
  updated_at: string;
}

/** Os dias que já têm relatório, do mais novo para o mais antigo, por WhatsApp. */
export async function listarRelatorios(limite = 120): Promise<Array<Pick<RelatorioDia, 'id' | 'connection_id' | 'conexao_nome' | 'atendente_nome' | 'dia' | 'nota' | 'conversas_analisadas'>>> {
  const { data, error } = await supabase
    .from('inbox_relatorio_diario')
    .select('id, connection_id, conexao_nome, atendente_nome, dia, nota, conversas_analisadas')
    .order('dia', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return (data ?? []) as any;
}

export async function carregarDia(connectionId: string, dia: string): Promise<{ relatorio: RelatorioDia | null; conversas: AnaliseConversaLinha[] }> {
  const [rel, conv] = await Promise.all([
    supabase.from('inbox_relatorio_diario').select('*').eq('connection_id', connectionId).eq('dia', dia).maybeSingle(),
    supabase.from('inbox_analise_conversa').select('*').eq('connection_id', connectionId).eq('dia', dia)
      .order('probabilidade', { ascending: false, nullsFirst: false }),
  ]);
  if (rel.error) throw rel.error;
  if (conv.error) throw conv.error;
  return { relatorio: (rel.data as RelatorioDia) ?? null, conversas: (conv.data ?? []) as AnaliseConversaLinha[] };
}

/**
 * Gera (ou refaz) um dia. A função trabalha ~2 min por chamada e diz quanto
 * falta; aqui se chama de novo até zerar, avisando o progresso.
 */
export async function gerarDia(
  dia: string,
  opcoes: { refazer: boolean; connectionId?: string | null; aoProgredir?: (pendentes: number) => void },
): Promise<void> {
  // O instante do clique: tudo analisado antes dele é refeito, mesmo que leve
  // várias chamadas — cada uma continua de onde a anterior parou.
  const refazerAntesDe = opcoes.refazer ? new Date().toISOString() : null;
  for (let rodada = 0; rodada < 15; rodada++) {
    const { data, error } = await supabase.functions.invoke('inbox-analise-diaria', {
      body: { dia, refazerAntesDe, connectionId: opcoes.connectionId ?? null },
    });
    if (error) {
      // O SDK devolve só "non-2xx status code"; o motivo está no corpo.
      const detalhe = await (error as any)?.context?.json?.().catch(() => null);
      throw new Error(detalhe?.error || 'Não foi possível gerar a análise.');
    }
    const pendentes = Number(data?.pendentes ?? 0);
    opcoes.aoProgredir?.(pendentes);
    if (!pendentes) return;
  }
  throw new Error('A análise ainda não terminou. Tente de novo em alguns minutos.');
}
